import {
    useState,
    useRef
} from "react";
import {
    useWindowDimensions,
    ScrollView,
    StyleSheet,
    View
} from "react-native";
import {
    WorkspaceSwitcher
} from "./brand";
import {
    type NCoreUIKitIcon,
    NCoreUIKitLocalize,
    type ComboboxValue,
    type ISelectBoxRef,
    type IComboboxRef,
    NCoreUIKitTheme,
    DateTimePicker,
    DropdownMenu,
    Breadcrumb,
    Pagination,
    TextInput,
    SelectBox,
    DataTable,
    Separator,
    CheckBox,
    Combobox,
    Sidebar,
    Avatar,
    Button,
    Badge,
    Empty,
    Table,
    Card,
    Tabs,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../i18n";
import {
    ShoppingBag,
    ChartColumn,
    LayoutGrid,
    Warehouse,
    PackageX,
    Download,
    Ellipsis,
    Settings,
    CircleX,
    Printer,
    Package,
    Search,
    Truck,
    Users,
    Plus,
    Bell,
    Eye
} from "lucide-react-native";
import {
    createKitIcon
} from "../components/kitIcons";

type StatusKey = "pending" | "preparing" | "shipped" | "delivered" | "returned";

type TabKey = "all" | StatusKey;

type ChannelKey = "web" | "marketplace" | "store";

type OrderRow = {
    channel: ChannelKey;
    status: StatusKey;
    payment: string;
    amount: number;
    email: string;
    name: string;
    date: Date;
    id: string;
};

type Option = {
    name: string;
    id: string;
};

const LayoutGridIcon = createKitIcon(LayoutGrid);

const ShoppingBagIcon = createKitIcon(ShoppingBag);

const PackageIcon = createKitIcon(Package);

const UsersIcon = createKitIcon(Users);

const ChartColumnIcon = createKitIcon(ChartColumn);

const TruckIcon = createKitIcon(Truck);

const WarehouseIcon = createKitIcon(Warehouse);

const SettingsIcon = createKitIcon(Settings);

const EllipsisIcon = createKitIcon(Ellipsis);

const SearchIcon = createKitIcon(Search);

const DownloadIcon = createKitIcon(Download);

const PlusIcon = createKitIcon(Plus);

const EyeIcon = createKitIcon(Eye);

const PrinterIcon = createKitIcon(Printer);

const CancelIcon = createKitIcon(CircleX);

const BellIcon = createKitIcon(Bell);

const EmptyIcon = createKitIcon(PackageX);

const PAGE_SIZE = 10;

const STATUS_TOTALS: Record<StatusKey, number> = {
    delivered: 1105,
    preparing: 18,
    pending: 32,
    shipped: 87,
    returned: 6
};

const STATUSES: Array<StatusKey> = [
    "pending",
    "preparing",
    "shipped",
    "delivered",
    "returned"
];

const TOTAL = STATUSES.reduce((sum, key) => sum + STATUS_TOTALS[key], 0);

const TAB_COUNT_LIMIT = 99;

const TABS: Array<{
    count?: number;
    key: TabKey;
}> = [
    {
        count: TOTAL,
        key: "all"
    },
    {
        count: STATUS_TOTALS.pending,
        key: "pending"
    },
    {
        count: STATUS_TOTALS.preparing,
        key: "preparing"
    },
    {
        count: STATUS_TOTALS.shipped,
        key: "shipped"
    },
    {
        key: "delivered"
    },
    {
        count: STATUS_TOTALS.returned,
        key: "returned"
    }
];

const INITIAL_STATUSES: Array<StatusKey> = [
    "pending",
    "shipped"
];

const INITIAL_DATE_RANGE = {
    start: new Date(2026, 9, 1),
    end: new Date(2026, 9, 31)
};

const CHANNELS: Array<"all" | ChannelKey> = [
    "all",
    "web",
    "marketplace",
    "store"
];

const BADGE_TYPES: Record<StatusKey, "warning" | "success" | "danger" | "neutral" | "info"> = {
    delivered: "success",
    pending: "warning",
    returned: "danger",
    preparing: "info",
    shipped: "neutral"
};

const ROWS: Array<OrderRow> = [
    {
        date: new Date(2026, 10, 1, 14, 30),
        email: "elif@nibgat.com",
        name: "Elif Demir",
        status: "pending",
        payment: "card",
        channel: "web",
        id: "NB-20418",
        amount: 1240
    },
    {
        date: new Date(2026, 10, 1, 11, 12),
        email: "mert.kaya@nibgat.com",
        channel: "marketplace",
        payment: "marketplace",
        status: "preparing",
        name: "Mert Kaya",
        id: "NB-20417",
        amount: 865.5
    },
    {
        date: new Date(2026, 9, 31, 19, 48),
        email: "zeynep@nibgat.com",
        name: "Zeynep Arslan",
        status: "shipped",
        payment: "card",
        channel: "web",
        id: "NB-20416",
        amount: 2390
    },
    {
        date: new Date(2026, 9, 31, 16, 5),
        email: "can.ozturk@nibgat.com",
        payment: "cashOnDelivery",
        status: "delivered",
        name: "Can Öztürk",
        channel: "store",
        id: "NB-20415",
        amount: 450
    },
    {
        date: new Date(2026, 9, 31, 10, 21),
        email: "ada@nibgat.com",
        name: "Ada Lovelace",
        payment: "transfer",
        status: "shipped",
        amount: 3115.75,
        channel: "web",
        id: "NB-20414"
    },
    {
        date: new Date(2026, 9, 30, 21, 37),
        email: "selin.y@nibgat.com",
        channel: "marketplace",
        payment: "marketplace",
        name: "Selin Yılmaz",
        status: "delivered",
        id: "NB-20413",
        amount: 729.9
    },
    {
        date: new Date(2026, 9, 30, 15, 2),
        email: "burak@nibgat.com",
        name: "Burak Şahin",
        status: "returned",
        channel: "store",
        payment: "card",
        id: "NB-20412",
        amount: 1080
    },
    {
        date: new Date(2026, 9, 30, 9, 44),
        email: "deniz.aydin@nibgat.com",
        name: "Deniz Aydın",
        status: "delivered",
        payment: "card",
        channel: "web",
        id: "NB-20411",
        amount: 5640
    },
    {
        date: new Date(2026, 9, 29, 18, 19),
        email: "ayse@nibgat.com",
        name: "Ayşe Karaca",
        payment: "transfer",
        status: "pending",
        channel: "web",
        id: "NB-20410",
        amount: 312
    },
    {
        date: new Date(2026, 9, 29, 13, 50),
        email: "emre.celik@nibgat.com",
        channel: "marketplace",
        payment: "marketplace",
        name: "Emre Çelik",
        status: "shipped",
        amount: 1755.25,
        id: "NB-20409"
    },
    {
        date: new Date(2026, 9, 29, 10, 5),
        email: "melis.koc@nibgat.com",
        name: "Melis Koç",
        status: "shipped",
        payment: "card",
        channel: "web",
        id: "NB-20408",
        amount: 980
    },
    {
        date: new Date(2026, 9, 28, 20, 41),
        email: "kerem@nibgat.com",
        channel: "marketplace",
        payment: "marketplace",
        name: "Kerem Aksoy",
        status: "pending",
        id: "NB-20407",
        amount: 2150.5
    },
    {
        date: new Date(2026, 9, 28, 17, 12),
        email: "derya.ates@nibgat.com",
        payment: "transfer",
        name: "Derya Ateş",
        status: "shipped",
        channel: "web",
        id: "NB-20406",
        amount: 640
    },
    {
        date: new Date(2026, 9, 28, 11, 58),
        payment: "cashOnDelivery",
        email: "onur@nibgat.com",
        name: "Onur Tekin",
        status: "pending",
        channel: "store",
        id: "NB-20405",
        amount: 375.4
    },
    {
        date: new Date(2026, 9, 27, 19, 26),
        email: "irem.polat@nibgat.com",
        name: "İrem Polat",
        status: "shipped",
        payment: "card",
        channel: "web",
        id: "NB-20404",
        amount: 4210
    }
];

const stylesheet = StyleSheet.create({
    header: {
        justifyContent: "space-between",
        alignItems: "center",
        flexDirection: "row",
        flexWrap: "wrap"
    },
    filters: {
        alignItems: "center",
        flexDirection: "row",
        flexWrap: "wrap"
    },
    bar: {
        alignItems: "center",
        flexDirection: "row"
    },
    person: {
        alignItems: "center",
        flexDirection: "row",
        flexShrink: 1,
        minWidth: 0
    },
    actions: {
        alignItems: "center",
        flexDirection: "row",
        flexWrap: "wrap"
    },
    personText: {
        flexShrink: 1,
        minWidth: 0
    },
    title: {
        flexShrink: 1,
        minWidth: 0
    },
    field: {
        flexGrow: 1,
        minWidth: 0
    },
    grow: {
        minWidth: 0,
        flex: 1
    },
    tabs: {
        minWidth: 0
    }
});

const handlePress = () => undefined;

const getOptionTitle = (option: Option) => option.name;

const getOptionKey = (option: Option) => option.id;

const Orders01 = () => {
    const {
        t
    } = useDocsI18n();

    const {
        activeLocale
    } = NCoreUIKitLocalize.useContext();

    const {
        controlHeights,
        shapeConfigs,
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        width
    } = useWindowDimensions();

    const statusRef = useRef<IComboboxRef>(null);

    const channelRef = useRef<ISelectBoxRef<Option>>(null);

    const [
        activePage,
        setActivePage
    ] = useState("orders");

    const [
        tab,
        setTab
    ] = useState<TabKey>("all");

    const [
        query,
        setQuery
    ] = useState("");

    const [
        statuses,
        setStatuses
    ] = useState<Array<StatusKey>>(INITIAL_STATUSES);

    const [
        channel,
        setChannel
    ] = useState<"all" | ChannelKey>("all");

    const [
        selectedIds,
        setSelectedIds
    ] = useState<Array<string>>([
        "NB-20416",
        "NB-20414"
    ]);

    const [
        page,
        setPage
    ] = useState(1);

    const integerFormat = new Intl.NumberFormat(activeLocale);

    const currency = new Intl.NumberFormat(activeLocale, {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
        style: "currency",
        currency: "TRY"
    });

    const dateFormat = new Intl.DateTimeFormat(activeLocale, {
        dateStyle: "medium",
        timeStyle: "short"
    });

    const rangeFormat = new Intl.DateTimeFormat(activeLocale, {
        year: "numeric",
        month: "short",
        day: "numeric"
    });

    const isCompact = width < shapeConfigs.compactBreakpoint;

    const isPaymentShown = width >= shapeConfigs.wideBreakpoint + shapeConfigs.drawerWidth + spaces.spacingXl;

    const gutter = isCompact ? spaces.spacingMd : spaces.spacingLg;

    const statusOptions: Array<Option> = STATUSES.map((key) => ({
        name: t(`blocks.orders01.status.${key}`),
        id: key
    }));

    const channelOptions: Array<Option> = CHANNELS.map((key) => ({
        name: t(`blocks.orders01.channel.${key}`),
        id: key
    }));

    const allChannel = [
        {
            __title: t("blocks.orders01.channel.all"),
            __key: "all"
        }
    ];

    const normalizedQuery = query.trim().toLocaleLowerCase(activeLocale);

    const isStatusVisible = (status: StatusKey) => (tab === "all" || status === tab) &&
        (statuses.length === 0 || statuses.includes(status));

    const filteredRows = ROWS.filter((row) => isStatusVisible(row.status) &&
        (channel === "all" || row.channel === channel) &&
        (normalizedQuery === "" || `${row.id} ${row.name}`.toLocaleLowerCase(activeLocale).includes(normalizedQuery)));

    const rows = filteredRows.slice(0, PAGE_SIZE);

    const totalItems = channel === "all" && normalizedQuery === "" ?
        STATUSES.filter(isStatusVisible).reduce((sum, key) => sum + STATUS_TOTALS[key], 0) :
        filteredRows.length;

    const selectedCount = rows.filter((row) => selectedIds.includes(row.id)).length;

    const isFiltered = tab !== "all" || channel !== "all" || query !== "" || statuses.length > 0;

    const handleTabChange = (value: string) => {
        setTab(value as TabKey);
        setPage(1);
    };

    const handleQueryChange = (value: string) => {
        setQuery(value);
        setPage(1);
    };

    const handleStatusChange = (value: ComboboxValue) => {
        setStatuses((Array.isArray(value) ? value : [
            value
        ]) as Array<StatusKey>);
        setPage(1);
    };

    const handleChannelChange = (items: Array<{
        __key: string;
    }>) => {
        setChannel((items[0]?.__key ?? "all") as "all" | ChannelKey);
        setPage(1);
    };

    const handleClear = () => {
        statusRef.current?.clearSelections();
        channelRef.current?.updateSelections(allChannel);
        setStatuses([]);
        setChannel("all");
        setQuery("");
        setTab("all");
        setPage(1);
    };

    const handleRangeTitle = (dateRange?: {
        start?: Date;
        end?: Date;
    }) => {
        if(!dateRange?.start) {
            return "";
        }

        return rangeFormat.formatRange(dateRange.start, dateRange.end ?? dateRange.start);
    };

    const handleRowSelect = (id: string) => () => setSelectedIds(
        selectedIds.includes(id) ? selectedIds.filter((key) => key !== id) : [
            ...selectedIds,
            id
        ]
    );

    const handleAllSelect = () => setSelectedIds(
        selectedCount === rows.length ? selectedIds.filter((id) => !rows.some((row) => row.id === id)) : [
            ...selectedIds,
            ...rows.map((row) => row.id).filter((id) => !selectedIds.includes(id))
        ]
    );

    const handleSelectionClose = () => setSelectedIds([]);

    const handlePage = (key: string) => () => setActivePage(key);

    const renderStatusPrefix = () => <Text
        variant="bodyMediumSize"
        color="mid"
    >
        {t("blocks.orders01.statusPrefix")}
    </Text>;

    const renderChannelPrefix = () => <Text
        variant="bodyMediumSize"
        color="mid"
    >
        {t("blocks.orders01.channelPrefix")}
    </Text>;

    const renderNavItem = (key: string, icon: NCoreUIKitIcon, title: string, count?: number) => <Sidebar.MenuItem
        key={key}
    >
        <Sidebar.MenuButton
            isActive={activePage === key}
            onPress={handlePage(key)}
            title={title}
            icon={icon}
        />
        {
            count === undefined ?
                null
            :
                <Sidebar.MenuBadge
                    count={count}
                />
        }
    </Sidebar.MenuItem>;

    const renderTab = (item: typeof TABS[number]) => <Tabs.Trigger
        count={item.count !== undefined && item.count > TAB_COUNT_LIMIT ? integerFormat.format(item.count) : item.count}
        title={t(`blocks.orders01.status.${item.key}`)}
        value={item.key}
        key={item.key}
    />;

    const renderRow = (row: OrderRow) => <Table.Row
        isSelected={selectedIds.includes(row.id)}
        key={row.id}
    >
        <Table.Cell
            isShrink={true}
        >
            <CheckBox
                accessibilityLabel={t("blocks.orders01.selectRow", [
                    row.id
                ])}
                isChecked={selectedIds.includes(row.id)}
                onPress={handleRowSelect(row.id)}
                spreadBehaviour="free"
            />
        </Table.Cell>
        <Table.Cell
            isShrink={true}
        >
            <Text
                variant="monoMediumSize"
                color="high"
            >
                {`#${row.id}`}
            </Text>
        </Table.Cell>
        <Table.Cell>
            <View
                style={[
                    stylesheet.person,
                    {
                        gap: spaces.spacingMd
                    }
                ]}
            >
                <Avatar
                    title={row.name}
                    size="xSmall"
                />
                <View
                    style={stylesheet.personText}
                >
                    <Text
                        variant="labelMediumSize"
                        numberOfLines={1}
                        color="high"
                    >
                        {row.name}
                    </Text>
                    <Text
                        variant="bodySmallSize"
                        numberOfLines={1}
                        color="mid"
                    >
                        {row.email}
                    </Text>
                </View>
            </View>
        </Table.Cell>
        <Table.Cell
            isShrink={true}
        >
            <Text
                variant="bodyMediumSize"
                numberOfLines={1}
                color="mid"
            >
                {dateFormat.format(row.date)}
            </Text>
        </Table.Cell>
        <Table.Cell
            isShrink={true}
        >
            <Badge
                title={t(`blocks.orders01.channel.${row.channel}`)}
                spreadBehaviour="free"
                variant="outline"
                type="neutral"
            />
        </Table.Cell>
        {
            isPaymentShown ?
                <Table.Cell
                    isShrink={true}
                >
                    <Text
                        variant="bodyMediumSize"
                        numberOfLines={1}
                        color="mid"
                    >
                        {t(`blocks.orders01.payment.${row.payment}`)}
                    </Text>
                </Table.Cell>
            :
                null
        }
        <Table.Cell
            isShrink={true}
        >
            <Badge
                title={t(`blocks.orders01.status.${row.status}`)}
                type={BADGE_TYPES[row.status]}
                spreadBehaviour="free"
                variant="tonal"
            />
        </Table.Cell>
        <Table.Cell
            isNumeric={true}
            isShrink={true}
        >
            {currency.format(row.amount)}
        </Table.Cell>
        <Table.Cell
            isShrink={true}
        >
            <DropdownMenu
                title={t("blocks.orders01.rowActions", [
                    row.id
                ])}
            >
                <DropdownMenu.Trigger>
                    <Button
                        accessibilityLabel={t("blocks.orders01.rowActions", [
                            row.id
                        ])}
                        spreadBehaviour="free"
                        onPress={handlePress}
                        icon={EllipsisIcon}
                        variant="ghost"
                        type="neutral"
                        size="small"
                    />
                </DropdownMenu.Trigger>
                <DropdownMenu.Content>
                    <DropdownMenu.Item
                        title={t("blocks.orders01.viewDetails")}
                        onPress={handlePress}
                        icon={EyeIcon}
                    />
                    <DropdownMenu.Item
                        title={t("blocks.orders01.downloadInvoice")}
                        onPress={handlePress}
                        icon={DownloadIcon}
                    />
                    <DropdownMenu.Item
                        title={t("blocks.orders01.printLabel")}
                        onPress={handlePress}
                        icon={PrinterIcon}
                    />
                    <DropdownMenu.Separator/>
                    <DropdownMenu.Item
                        title={t("blocks.orders01.cancelOrder")}
                        onPress={handlePress}
                        icon={CancelIcon}
                        type="danger"
                    />
                </DropdownMenu.Content>
            </DropdownMenu>
        </Table.Cell>
    </Table.Row>;

    const renderTable = () => {
        if(rows.length === 0) {
            return <Empty
                variant="outline"
            >
                <Empty.Header>
                    <Empty.Media
                        icon={EmptyIcon}
                    />
                    <Empty.Title>
                        {t("blocks.orders01.emptyTitle")}
                    </Empty.Title>
                    <Empty.Description>
                        {t("blocks.orders01.emptyDescription")}
                    </Empty.Description>
                </Empty.Header>
                <Empty.Content>
                    <Button
                        title={t("blocks.orders01.clearFilters")}
                        spreadBehaviour="free"
                        onPress={handleClear}
                        variant="outline"
                        type="neutral"
                    />
                </Empty.Content>
            </Empty>;
        }

        return <Card
            variant="outline"
        >
            <Card.Content
                isFlush={true}
            >
                <Table
                    caption={t("blocks.orders01.tableCaption")}
                    isCaptionHidden={true}
                    isRaised={true}
                    size="large"
                >
                    <Table.Header>
                        <Table.Row>
                            <Table.Head
                                isShrink={true}
                            >
                                <CheckBox
                                    isIndeterminate={selectedCount > 0 && selectedCount < rows.length}
                                    accessibilityLabel={t("blocks.orders01.selectAll")}
                                    isChecked={selectedCount === rows.length}
                                    onPress={handleAllSelect}
                                    spreadBehaviour="free"
                                />
                            </Table.Head>
                            <Table.Head
                                isShrink={true}
                            >
                                {t("blocks.orders01.columnOrder")}
                            </Table.Head>
                            <Table.Head>
                                {t("blocks.orders01.columnCustomer")}
                            </Table.Head>
                            <Table.Head
                                isShrink={true}
                            >
                                {t("blocks.orders01.columnDate")}
                            </Table.Head>
                            <Table.Head
                                isShrink={true}
                            >
                                {t("blocks.orders01.columnChannel")}
                            </Table.Head>
                            {
                                isPaymentShown ?
                                    <Table.Head
                                        isShrink={true}
                                    >
                                        {t("blocks.orders01.columnPayment")}
                                    </Table.Head>
                                :
                                    null
                            }
                            <Table.Head
                                isShrink={true}
                            >
                                {t("blocks.orders01.columnStatus")}
                            </Table.Head>
                            <Table.Head
                                isNumeric={true}
                                isShrink={true}
                            >
                                {t("blocks.orders01.columnAmount")}
                            </Table.Head>
                            <Table.Head
                                accessibilityLabel={t("blocks.orders01.columnActions")}
                                isShrink={true}
                            />
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {rows.map(renderRow)}
                    </Table.Body>
                </Table>
            </Card.Content>
        </Card>;
    };

    const renderSelection = () => <DataTable.SelectionBar
        actions={[
            {
                accessibilityLabel: t("blocks.orders01.ship"),
                title: t("blocks.orders01.ship"),
                onPress: handlePress,
                icon: TruckIcon
            },
            {
                accessibilityLabel: t("blocks.orders01.printInvoice"),
                title: t("blocks.orders01.printInvoice"),
                onPress: handlePress,
                icon: PrinterIcon
            }
        ]}
        accessibilityLabel={t("blocks.orders01.bulkActions")}
        onClose={handleSelectionClose}
        selectedCount={selectedCount}
    />;

    return <Sidebar.Provider
        variant="inset"
    >
        <Sidebar
            title={t("blocks.orders01.navTitle")}
            collapseBehaviour="offcanvas"
        >
            <Sidebar.Header>
                <WorkspaceSwitcher
                    onPress={handlePress}
                />
            </Sidebar.Header>
            <Sidebar.Content>
                <Sidebar.Group>
                    <Sidebar.Menu>
                        {renderNavItem("overview", LayoutGridIcon, t("blocks.orders01.navOverview"))}
                        {renderNavItem("orders", ShoppingBagIcon, t("blocks.orders01.navOrders"), STATUS_TOTALS.pending)}
                        {renderNavItem("products", PackageIcon, t("blocks.orders01.navProducts"))}
                        {renderNavItem("customers", UsersIcon, t("blocks.orders01.navCustomers"))}
                        {renderNavItem("analytics", ChartColumnIcon, t("blocks.orders01.navAnalytics"))}
                    </Sidebar.Menu>
                </Sidebar.Group>
                <Sidebar.Group
                    title={t("blocks.orders01.operations")}
                >
                    <Sidebar.Menu>
                        {renderNavItem("shipping", TruckIcon, t("blocks.orders01.navShipping"))}
                        {renderNavItem("stock", WarehouseIcon, t("blocks.orders01.navStock"))}
                        {renderNavItem("settings", SettingsIcon, t("blocks.orders01.navSettings"))}
                    </Sidebar.Menu>
                </Sidebar.Group>
            </Sidebar.Content>
            <Sidebar.Footer>
                <Sidebar.Menu>
                    <Sidebar.MenuItem>
                        <Sidebar.MenuButton
                            renderIcon={() => <Avatar
                                title={t("blocks.orders01.userName")}
                                size="xSmall"
                            />}
                            subTitle={t("blocks.orders01.userEmail")}
                            title={t("blocks.orders01.userName")}
                        />
                        <Sidebar.MenuAction
                            title={t("blocks.orders01.userMenu")}
                            onPress={handlePress}
                            isShowOnHover={false}
                            icon={EllipsisIcon}
                        />
                    </Sidebar.MenuItem>
                </Sidebar.Menu>
            </Sidebar.Footer>
        </Sidebar>
        <Sidebar.Inset>
            <View
                style={[
                    stylesheet.bar,
                    {
                        paddingVertical: spaces.spacingMd,
                        paddingHorizontal: gutter,
                        gap: spaces.spacingSm
                    }
                ]}
            >
                <Sidebar.Trigger/>
                {
                    isCompact ?
                        null
                    :
                        <Breadcrumb
                            accessibilityLabel={t("blocks.orders01.breadcrumb")}
                        >
                            <Breadcrumb.List>
                                <Breadcrumb.Item>
                                    <Breadcrumb.Link
                                        title={t("blocks.orders01.sales")}
                                        onPress={handlePress}
                                    />
                                </Breadcrumb.Item>
                                <Breadcrumb.Item>
                                    <Breadcrumb.Page
                                        title={t("blocks.orders01.navOrders")}
                                    />
                                </Breadcrumb.Item>
                            </Breadcrumb.List>
                        </Breadcrumb>
                }
                <View
                    style={stylesheet.grow}
                />
                <Badge.Indicator
                    spreadBehaviour="free"
                    type="danger"
                >
                    <Button
                        accessibilityLabel={t("blocks.orders01.notifications")}
                        spreadBehaviour="free"
                        onPress={handlePress}
                        variant="ghost"
                        icon={BellIcon}
                        type="neutral"
                        size="small"
                    />
                </Badge.Indicator>
            </View>
            <Separator/>
            <ScrollView
                contentContainerStyle={{
                    paddingBottom: gutter + (selectedCount > 0 ? spaces.spacingLg * 2 + controlHeights.small + spaces.spacingSm * 2 : 0),
                    paddingHorizontal: gutter,
                    gap: spaces.spacingLg,
                    paddingTop: gutter
                }}
                style={stylesheet.grow}
            >
                <View
                    style={[
                        stylesheet.header,
                        {
                            gap: spaces.spacingMd
                        }
                    ]}
                >
                    <View
                        style={[
                            stylesheet.title,
                            {
                                gap: spaces.spacingXs
                            }
                        ]}
                    >
                        <Text
                            variant="headlineMediumSize"
                            accessibilityRole="header"
                            color="high"
                        >
                            {t("blocks.orders01.navOrders")}
                        </Text>
                        <Text
                            variant="bodyMediumSize"
                            color="mid"
                        >
                            {t("blocks.orders01.description")}
                        </Text>
                    </View>
                    <View
                        style={[
                            stylesheet.actions,
                            {
                                gap: spaces.spacingSm
                            }
                        ]}
                    >
                        <Button
                            title={t("blocks.orders01.export")}
                            spreadBehaviour="free"
                            onPress={handlePress}
                            icon={DownloadIcon}
                            variant="outline"
                            type="neutral"
                        />
                        <Button
                            title={t("blocks.orders01.create")}
                            spreadBehaviour="free"
                            onPress={handlePress}
                            variant="filled"
                            icon={PlusIcon}
                            type="primary"
                        />
                    </View>
                </View>
                <Tabs
                    onValueChange={handleTabChange}
                    style={stylesheet.tabs}
                    variant="line"
                    value={tab}
                >
                    <Tabs.List
                        title={t("blocks.orders01.statusTitle")}
                    >
                        {TABS.map(renderTab)}
                    </Tabs.List>
                </Tabs>
                <View
                    style={[
                        stylesheet.filters,
                        {
                            gap: spaces.spacingSm
                        }
                    ]}
                >
                    <TextInput
                        accessibilityLabel={t("blocks.orders01.searchPlaceholder")}
                        placeholder={t("blocks.orders01.searchPlaceholder")}
                        style={[
                            stylesheet.field,
                            {
                                flexBasis: shapeConfigs.fieldMinWidth
                            }
                        ]}
                        onChangeText={handleQueryChange}
                        spreadBehaviour="free"
                        isClearEnabled={true}
                        icon={SearchIcon}
                        value={query}
                    />
                    <Combobox
                        accessibilityLabel={t("blocks.orders01.statusFilter")}
                        placeholder={t("blocks.orders01.statusAll")}
                        style={[
                            stylesheet.field,
                            {
                                flexBasis: shapeConfigs.drawerWidth
                            }
                        ]}
                        CustomPrefixComponent={renderStatusPrefix}
                        onValueChange={handleStatusChange}
                        defaultValue={INITIAL_STATUSES}
                        titleExtractor={getOptionTitle}
                        keyExtractor={getOptionKey}
                        selectBehaviour="multiple"
                        spreadBehaviour="free"
                        data={statusOptions}
                        ref={statusRef}
                    />
                    <SelectBox
                        accessibilityLabel={t("blocks.orders01.channelTitle")}
                        style={[
                            stylesheet.field,
                            {
                                flexBasis: shapeConfigs.fieldMinWidth,
                                flexGrow: isCompact ? 1 : 0
                            }
                        ]}
                        CustomPrefixComponent={renderChannelPrefix}
                        titleExtractor={getOptionTitle}
                        onChange={handleChannelChange}
                        keyExtractor={getOptionKey}
                        spreadBehaviour="free"
                        data={channelOptions}
                        defaultValue="all"
                        ref={channelRef}
                    />
                    <DateTimePicker
                        style={[
                            stylesheet.field,
                            {
                                flexBasis: shapeConfigs.fieldMinWidth,
                                flexGrow: isCompact ? 1 : 0
                            }
                        ]}
                        initialDateRange={INITIAL_DATE_RANGE}
                        dateRangeTitle={handleRangeTitle}
                        spreadBehaviour="free"
                        pickerType="date"
                        variant="range"
                    />
                    <Button
                        title={t("blocks.orders01.clearFilters")}
                        isDisabled={!isFiltered}
                        spreadBehaviour="free"
                        onPress={handleClear}
                        variant="ghost"
                        type="neutral"
                    />
                </View>
                {renderTable()}
                <Pagination
                    unitTitle={t("blocks.orders01.unit")}
                    stepTitleBehaviour="hidden"
                    totalItems={totalItems}
                    onPageChange={setPage}
                    pageSize={PAGE_SIZE}
                    siblingCount={0}
                    size="small"
                    page={page}
                >
                    <Pagination.Range/>
                    <Pagination.Content/>
                </Pagination>
            </ScrollView>
            {renderSelection()}
        </Sidebar.Inset>
    </Sidebar.Provider>;
};
export default Orders01;
